import { useContext } from 'react';
import { useSelector } from 'react-redux';

import { RELATIONSHIP_TYPES } from '@constants/common/relationShipTypes';
import { ENROLLMENT_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import { EnrollmentContext } from '@modules/members/enrollments/context/MemberMyEnrollmentProvider';
import { getCurrency } from '@utils/tools/currencyTools';

const { BROKER, AGENCY_MANAGER, APPROVEDADMINSTAFFL1, APPROVEDADMINSTAFFL2, ASSISTER } = ALL_USER_ROLES;

type Permissions = string[];
type UserRole = string;

export const usePermissions = () => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const { watch } = enrollmentPremiumFormContext;
    const values = watch && watch();
    const { userPermissions } = permissions || {};

    const isWithinEffectiveRange = (month: number, values) =>
        month >= values.enrollmentBenefitEffectiveStartMonth && month <= values.enrollmentBenefitEffectiveEndMonth;

    const isNotEqualEffectiveDate = values =>
        values.enrollmentBenefitEffectiveStartDateUI !== values.enrollmentBenefitEffectiveEndDateUI;

    const isPremiumMonthNotInOrignalEffectiveDate = (month: number) =>
        month < selectedEnrollment.enrollmentBenefitEffectiveStartMonth ||
        month > selectedEnrollment.enrollmentBenefitEffectiveEndMonth;

    const isPremiumMonthInOrignalEffectiveDate = (month: number) =>
        month >= selectedEnrollment.enrollmentBenefitEffectiveStartMonth &&
        month <= selectedEnrollment.enrollmentBenefitEffectiveEndMonth;

    const isNotApplicable = (premium: { month: number }, values) => {
        return isWithinEffectiveRange(premium.month, values) && isNotEqualEffectiveDate(values);
    };

    const showPremiumValue = (premium: { month: number }, values) =>
        isWithinEffectiveRange(premium.month, values) && isNotEqualEffectiveDate;

    const ehbPremiumValue = (premium, enrollment) => {
        if (!showPremiumValue(premium, enrollment)) {
            return '';
        }
        const { ehbPercentage } = enrollment;
        if (ehbPercentage !== null && ehbPercentage > 0 && ehbPercentage <= 1) {
            return getCurrency(premium.grossPremiumAmount * ehbPercentage);
        }
        return getCurrency(premium.grossPremiumAmount);
    };

    const isReinstatementPermission = (permissions: Permissions): boolean =>
        permissions.includes('MYAPP_REINST_ENROLLMENT');

    const isOverideEnrollmentPermission = (permissions: Permissions): boolean =>
        permissions.includes('MYAPP_ENRLMNT_OVRRD_ENRL_TO_CONFIRM');

    const isAgencyUser = (loggedInUserRole: UserRole): boolean =>
        [BROKER, AGENCY_MANAGER, APPROVEDADMINSTAFFL1, APPROVEDADMINSTAFFL2, ASSISTER].includes(loggedInUserRole);

    const isPremiumEditPermission = (userPermissions): boolean =>
        userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable;

    const isMemberEnrlmntAptcEditable = userPermissions =>
        userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable;

    const isEnrlmntGroupMaxAptcEditable = (userPermissions): boolean =>
        userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable;

    const isEnrollmentPendingOrPendCancelled = (enrollment): boolean =>
        enrollment?.enrollmentStatus === ENROLLMENT_STATUSES.PEND ||
        enrollment?.enrollmentStatus === ENROLLMENT_STATUSES.PEND_CANCELLED;

    const isMemberEnrlmntEditSlcsps = (userPermissions): boolean =>
        userPermissions?.memberEnrlmntViewSlcsp || userPermissions?.memberEnrlmntEditSlcsp;

    const canResend834History = (userPermissions, enrollment) =>
        userPermissions?.memberEnrlmnt834hist &&
        ![ENROLLMENT_STATUSES.PEND, ENROLLMENT_STATUSES.PEND_CANCELLED].includes(enrollment?.enrollmentStatus);

    const canResendLatest834Transaction = (userPermissions, enrollment) =>
        userPermissions?.memberEnrlmntLtst834 &&
        enrollment.enrollmentStatus !== ENROLLMENT_STATUSES.PEND &&
        enrollment.enrollmentStatus !== ENROLLMENT_STATUSES.PEND_CANCELLED;

    const iseEnrolleeNameEditable = (userPermissions, enrollee) => {
        return (
            (!userPermissions?.memberEnrlmntMembrNmEditable && isSelfEnrollment(enrollee)) ||
            (!userPermissions?.memberEnrlmntMembrDpndntNmEditable && !isSelfEnrollment(enrollee))
        );
    };

    const isEnrolleeGenederEditable = (userPermissions, enrollee, enrollment) =>
        (!userPermissions?.memberEnrlmntMembrGndrEditable && isSelfEnrollment(enrollee)) ||
        (!userPermissions?.memberEnrlmntMembrDpndntGndrEditable && !isSelfEnrollment(enrollee)) ||
        enrollee.enrolleeEffectiveStartDateUI === enrollee.enrolleeEffectiveEndDateUI ||
        enrollment.enrollmentBenefitEffectiveStartDateUI === enrollment.enrollmentBenefitEffectiveEndDateUI;

    const isSelfEnrollment = enrollee =>
        enrollee.relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value;

    const viewEditableFields = (userPermissions, values, premium) =>
        isEnrlmntGroupMaxAptcEditable(userPermissions) &&
        isNotEqualEffectiveDate(values) &&
        isWithinEffectiveRange(premium.month, values);

    const viewSlcspField = (values, premium) =>
        isNotEqualEffectiveDate(values) && isWithinEffectiveRange(premium.month, values);

    const isEnrollmentActionsPermission = (permissions, enrollment) => {
        return (
            (permissions.includes('MYAPP_ENRLMNT_OVRRD_ENRL_TO_CONFIRM') &&
                [ENROLLMENT_STATUSES.PENDING, ENROLLMENT_STATUSES.PEND].includes(enrollment.enrollmentStatus) &&
                !enrollment.enrollmentConfirmationDate) ||
            (permissions.includes('MYAPP_REINST_ENROLLMENT') &&
                [ENROLLMENT_STATUSES.CANCEL, ENROLLMENT_STATUSES.TERMINATED].includes(enrollment.enrollmentStatus))
        );
    };

    const viewEnrollmentPremiumHistoryAction = enrollment => {
        return (
            userPermissions?.memberPrmHistActions &&
            (userPermissions?.memberPrmHistCancel || userPermissions?.memberPrmHistChange) &&
            enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND &&
            enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND_CANCELLED
        );
    };

    const viewApplicationType = (premium, enrollment) =>
        isWithinEffectiveRange(premium.month, enrollment) && isNotEqualEffectiveDate(enrollment);

    const isPendingCalculation = (month, values) => {
        return (
            isWithinEffectiveRange(month, values) &&
            isPremiumMonthNotInOrignalEffectiveDate(month) &&
            isNotEqualEffectiveDate(values)
        );
    };

    return {
        isOverideEnrollmentPermission,
        isEnrollmentActionsPermission,
        isReinstatementPermission,
        isAgencyUser,
        isPremiumEditPermission,
        isMemberEnrlmntAptcEditable,
        isEnrlmntGroupMaxAptcEditable,
        isEnrollmentPendingOrPendCancelled,
        isMemberEnrlmntEditSlcsps,
        canResend834History,
        canResendLatest834Transaction,
        iseEnrolleeNameEditable,
        isEnrolleeGenederEditable,
        isSelfEnrollment,
        viewEditableFields,
        viewEnrollmentPremiumHistoryAction,
        viewSlcspField,
        viewApplicationType,
        isPremiumMonthNotInOrignalEffectiveDate,
        ehbPremiumValue,
        isNotApplicable,
        showPremiumValue,
        isNotEqualEffectiveDate,
        isWithinEffectiveRange,
        isPendingCalculation,
        isPremiumMonthInOrignalEffectiveDate,
    };
};
